import React from 'react'
import { Loader } from '../../shared/components/Loader'
import { Button, Input, Form, Label } from 'semantic-ui-react'
import { TooltipLabel } from 'components/TooltipLabel'
import { toast } from 'libs/toast'
import ProFeatureLabel from 'components/ProFeatureLabel'
import constants from 'config/constants'
import { AllowPermission } from 'shared/components/AllowPermission'
import usePermission from 'hooks/usePermission'

export default function SingleSignOn({
  connection,
  isConnectionLoading,
  hasPortalLinkError,
  isPortalLinkLoading,
  handleOnClick,
  settings,
  setSettings,
  handleSubmit,
  isEnabledSSO,
  checkingSSOFeatureToggle,
}) {
  const { hasPermission } = usePermission()
  const hasManageAuthenSecurityPermission = hasPermission(
    constants.permissions.manageAuthenticationSecurity
  )

  function copyTextToclipBoard(event) {
    event.preventDefault()
    if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(connection.authorization_url)
      toast.success('Boom! Authorization URL copied to clipboard.')
    } else {
      toast.error('The Clipboard API is not available.')
    }
  }

  function getContent() {
    if (checkingSSOFeatureToggle || isConnectionLoading) {
      return <Loader withContainer />
    } else if (isEnabledSSO && !hasPortalLinkError && hasManageAuthenSecurityPermission) {
      if (connection && connection.status === 'active') {
        return (
          <React.Fragment>
            <div className="mt3">
              <Button
                disabled={isPortalLinkLoading}
                loading={isPortalLinkLoading}
                onClick={handleOnClick}
              >
                Edit SSO configuration
              </Button>
            </div>
            {settings && (
              <React.Fragment>
                <div className="mt4">
                  <div className="grouped fields">
                    <h3>Authorization URL</h3>
                    <p>Share this URL with your team to create accounts on the fly.</p>
                    <Input
                      style={{ width: 440 }}
                      action={{
                        icon: 'far fa-clone',
                        onClick: (event) => copyTextToclipBoard(event)
                      }}
                      value={connection.authorization_url}
                      readOnly
                    />
                  </div>
                </div>
                <div className="mt4">
                  <div className="grouped fields">
                    <h3>Authorized domains</h3>
                    <p>
                      Need to authorize more domains? Please{' '}
                      <a id="add-authorization-domain" href="#">
                        contact support
                      </a>{' '}
                      to update your domain list.
                    </p>
                  </div>
                </div>
                <div className="mt4">
                  <h3>
                    <TooltipLabel
                      label="SSO enforcement"
                      descriptionTitle="SSO enforcement"
                      descriptionContent={
                        <div>
                          <p>Configure how SSO is applied across your user base.</p>
                          <ul>
                            <li>
                              <strong>All users</strong> - Automatically applies SSO to everyone
                              except those turned off.
                            </li>
                            <li>
                              <strong>Optional</strong> - Users choose if they want SSO or not.
                            </li>
                          </ul>
                        </div>
                      }
                    />
                  </h3>
                  <p>Control how Single Sign-On (SSO) is applied within your organization.</p>
                  <Form>
                    <Form.Group grouped className="!mb-5">
                      <Form.Checkbox
                        checked={settings.require_sso === true}
                        onChange={() => setSettings((prev) => ({ ...prev, require_sso: true }))}
                        radio
                        label="All users, except users manually deactivated"
                      />
                      <Form.Checkbox
                        checked={settings.require_sso === false}
                        onChange={() => setSettings((prev) => ({ ...prev, require_sso: false }))}
                        radio
                        label="It's optional"
                      />
                    </Form.Group>
                  </Form>
                  <Button primary onClick={handleSubmit}>
                    Save settings
                  </Button>
                </div>
              </React.Fragment>
            )}
          </React.Fragment>
        )
      } else {
        return (
          <AllowPermission permission={constants.permissions.manageAuthenticationSecurity}>
            <div className="mt3">
              <Button
                disabled={isPortalLinkLoading}
                loading={isPortalLinkLoading}
                onClick={handleOnClick}
                primary
              >
                Configure SSO
              </Button>
            </div>
          </AllowPermission>
        )
      }
    } else {
      return null
    }
  }

  return (
    <React.Fragment>
      <h2 className="flex items-center ui header">
        Single sign-on (SSO)
        {isEnabledSSO && connection && connection.status === 'active' && (
          <Label basic className="green">
            Active
          </Label>
        )}
        <ProFeatureLabel
          title="Single sign-on (SSO)"
          content="Single sign-on (SSO) is available on a higher plan. Upgrade to unlock this feature."
          learnMoreLink="https://help.simplesat.io/en/articles/8340321-enterprise-sso-single-sign-on"
          shouldShowBillingPageLink={!checkingSSOFeatureToggle && !isEnabledSSO}
        />
      </h2>

      <p>
        Enable seamless access through your organization's Identity Provider (IdP), compatible with
        both SAML and OIDC protocols.
      </p>
      <a
        target="_blank"
        rel="noreferrer"
        href="https://help.simplesat.io/en/articles/8340321-enterprise-sso-single-sign-on"
      >
        <i className="icon far fa-book" />
        Learn more about SSO
      </a>
      {getContent()}
    </React.Fragment>
  )
}



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/components/SingleSignOn/index.js